import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { Calendar, UserCheck } from 'lucide-react';
import { getTimeSlots } from '../utils/helpers';

const FollowUpModal = ({ isOpen, onClose, appointment, onFollowUp, user }) => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchServices();
    }
  }, [isOpen]);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data.filter(s => s.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch services');
    }
  };

  const handleCreateFollowUp = () => {
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    if (!followUpDate) {
      toast.error('Please select a date');
      return;
    }

    const service = services.find(s => s.id === selectedService);
    if (service?.type === 'time_based' && !followUpTime) {
      toast.error('Please select a time for time-based appointment');
      return;
    }

    // Pass data to parent
    onFollowUp({
      originalAppointmentId: appointment.id,
      patient_id: appointment.patient_id,
      service_id: selectedService,
      scheduled_date: followUpDate,
      scheduled_time: service?.type === 'time_based' ? followUpTime : null
    });
  };

  const resetForm = () => {
    setSelectedService('');
    setFollowUpDate('');
    setFollowUpTime('');
  };

  if (!appointment) return null;

  const selectedServiceObj = services.find(s => s.id === selectedService);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
        resetForm();
      }
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-emerald-600" />
            Schedule Follow-up Appointment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-emerald-50 rounded-lg">
            <p className="text-sm text-emerald-800">
              <strong>Patient:</strong> {appointment.patient_name}
            </p>
            <p className="text-sm text-emerald-600 mt-1">
              Creating follow-up appointment for this patient
            </p>
          </div>

          <div>
            <Label htmlFor="service">Service</Label>
            <select
              id="service"
              data-testid="followup-service-select"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="mt-2 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Select a service</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="followUpDate">Date</Label>
            <Input
              id="followUpDate"
              data-testid="followup-date-input"
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="mt-2"
            />
          </div>

          {selectedServiceObj?.type === 'time_based' && (
            <div>
              <Label htmlFor="followUpTime">Time</Label>
              <select
                id="followUpTime"
                data-testid="followup-time-select"
                value={followUpTime}
                onChange={(e) => setFollowUpTime(e.target.value)}
                className="mt-2 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select a time</option>
                {getTimeSlots().map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          )}

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => {
            onClose();
            resetForm();
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFollowUp}
            data-testid="followup-submit-button"
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
          >
            Create Follow-up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FollowUpModal;
